import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { API_ENDPOINTS } from '../endpoints';
import { PaginatedResponse } from '@/api/types.ts';

export interface Transaction {
    id: string;
    customer_id: string;
    trading_account_id: string;
    type: 'deposit' | 'withdrawal';
    amount: number;
    currency: string;
    status: 'pending' | 'completed' | 'failed' | 'cancelled';
    payment_method?: string;
    reference_number?: string;
    description?: string;
    notes?: string;
    approved_by?: string;
    approved_at?: string;
    rejected_by?: string;
    rejected_at?: string;
    rejection_reason?: string;
    created_by?: string;
    created_by_type?: 'customer' | 'crm_user';
    created_by_name?: string;
    metadata?: Record<string, any>;
    created_at: string;
    updated_at: string;
    customer?: {
        id: string;
        name: string;
        email: string;
    };
    trading_account?: {
        id: string;
        base_currency: string;
    };
}

export interface TransactionFilters {
    customer_id?: string;
    trading_account_id?: string;
    type?: 'deposit' | 'withdrawal' | 'bonus';
    status?: 'pending' | 'completed' | 'failed' | 'cancelled';
    from_date?: string;
    to_date?: string;
    search?: string;
    payment_method?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    per_page?: number;
    page?: number;
}

export interface CreateTransactionRequest {
    customer_id: string;
    trading_account_id: string;
    type: 'deposit' | 'withdrawal';
    amount: number;
    currency: string;
    payment_method?: string;
    reference_number?: string;
    description?: string;
    notes?: string;
}

export interface UpdateTransactionRequest {
    status?: 'pending' | 'completed' | 'failed' | 'cancelled';
    notes?: string;
    rejection_reason?: string;
}

// Get transactions for a customer
const fetchCustomerTransactions = async (customerId: string, filters?: TransactionFilters) => {
    // Ensure we use the CRM endpoint, not the trader/customer endpoint
    // The endpoint should be: /crm/customers/{customerId}/transactions
    const endpoint = API_ENDPOINTS.CRM.CUSTOMERS.TRANSACTIONS(customerId);

    // Validate endpoint starts with /crm to ensure we're using CRM endpoints
    if (!endpoint.startsWith('/crm/')) {
        console.error('Invalid endpoint for customer transactions:', endpoint);
        throw new Error(`Invalid endpoint: ${endpoint}. Expected CRM endpoint.`);
    }

    const { data } = await httpClient.get<PaginatedResponse<Transaction>>(endpoint, {
        params: filters,
    });
    return data;
};

// Get transactions for the current trader (portal view)
const fetchTransactions = async (filters?: TransactionFilters) => {
    const { data } = await httpClient.get<ApiResponse<{ data: Transaction[]; meta: any }>>(
        API_ENDPOINTS.TRADER.TRANSACTIONS.LIST,
        { params: filters }
    );
    return data;
};

export const useCustomerTransactions = (customerId: string, filters?: TransactionFilters) => {
    return useQuery({
        queryKey: ['customer-transactions', customerId, filters],
        queryFn: () => fetchCustomerTransactions(customerId, filters),
        enabled: !!customerId,
    });
};

// Get transactions for the current trader (portal view)
export const useTransactions = (filters?: TransactionFilters) => {
    return useQuery({
        queryKey: ['transactions', filters],
        queryFn: () => fetchTransactions(filters),
    });
};

// Create a new transaction
export const useCreateTransaction = (customerId: string) => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<Transaction>, Error, CreateTransactionRequest>({
        mutationFn: async data => {
            const response = await httpClient.post<ApiResponse<Transaction>>(
                API_ENDPOINTS.CRM.CUSTOMERS.TRANSACTIONS(customerId),
                data
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customer-transactions', customerId] });
            queryClient.invalidateQueries({ queryKey: ['customer-accounts', customerId] });
        },
    });
};

// Update transaction (notes, status, rejection_reason) - uses global CRM transactions endpoint
export const useUpdateTransaction = (customerId: string) => {
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<Transaction>,
        Error,
        { transactionId: string; data: UpdateTransactionRequest }
    >({
        mutationFn: async ({ transactionId, data }) => {
            const response = await httpClient.put<ApiResponse<Transaction>>(
                API_ENDPOINTS.CRM.TRANSACTIONS.UPDATE(transactionId),
                data
            );
            return response.data;
        },
        onSuccess: (_, { transactionId }) => {
            queryClient.invalidateQueries({ queryKey: ['customer-transactions', customerId] });
        },
    });
};

// Delete a transaction - uses global CRM transactions endpoint
export const useDeleteTransaction = (customerId: string) => {
    const queryClient = useQueryClient();

    return useMutation<ApiResponse<void>, Error, string>({
        mutationFn: async transactionId => {
            const response = await httpClient.delete<ApiResponse<void>>(
                API_ENDPOINTS.CRM.TRANSACTIONS.DELETE(transactionId)
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customer-transactions', customerId] });
        },
    });
};
